import type { UseFormReturn } from "react-hook-form";
import type { z } from "zod";

import { MapPin } from "lucide-react";

import type { CourierOfficeQuery, CouriersQueryResponse } from "@/api/courier-query";
import type { State } from "@/api/util";
import type { Courier } from "@/types/courier";
import type { UseQueryResult } from "@tanstack/react-query";

import type { pickupSchema } from "./order-receipt";

import SearchDropdown from "../dashboard/search-dropdown";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Form, FormControl, FormField, FormItem, FormMessage } from "../ui/form";
import { Label } from "../ui/label";

interface PickupLocationProps {
	form: UseFormReturn<z.infer<typeof pickupSchema>, any, undefined>;
	handleSubmit: (values: z.infer<typeof pickupSchema>) => void;
	statesQuery: UseQueryResult<State[], Error>;
	courierQuery: UseQueryResult<CouriersQueryResponse, Error>;
	courierOfficeQuery: UseQueryResult<CourierOfficeQuery, Error>;

}
export function PickupLocation({ form, statesQuery, courierOfficeQuery, courierQuery, handleSubmit }: PickupLocationProps) {
	const courierRender = (item: Courier) => (
		<div className="flex items-center gap-3">
			<div className="p-2 bg-gray-100 rounded-lg">
				<MapPin size={20} />
			</div>
			<span className="capitalize">{item.name.toLowerCase()}</span>
		</div>
	);

	return (
		<>
			<DialogHeader className="flex-row items-start justify-between space-y-0">
				<DialogTitle>Pickup Location</DialogTitle>
			</DialogHeader>
			<DialogDescription>
				Receive your orders easily by using Intasida locations nearest to you
			</DialogDescription>

			<Form {...form}>
				{/*  @ts-expect-error - form is not typed */}
				<form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
					<FormField
						// @ts-expect-error - form is not typed
						control={form.control}
						name="state"
						render={({ field }) => (
							<FormItem>
								<FormControl>
									{/* <SearchStates field={field} states={states.data} isPending={states.isPending} isError={states.isError} {...field} /> */}

									<SearchDropdown
										field={field}
										data={statesQuery.data}
										isPending={statesQuery.isPending}
										isError={statesQuery.isError}
										placeholder="Select state"
										searchPlaceholder="Search state"
										renderItem={courierRender}
										filterFunction={(item: State, query: string) => item.name.toLowerCase().includes(query.toLowerCase())}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						// @ts-expect-error - form is not typed
						control={form.control}
						name="courierService"
						render={({ field }) => (
							<FormItem>
								<FormControl>
									<SearchDropdown
										field={field}
										data={courierQuery.data?.data.items}
										isPending={courierQuery.isPending}
										isError={courierQuery.isError}
										placeholder="Select courier service"
										searchPlaceholder="Search courier service"
										renderItem={courierRender}
										filterFunction={(item: Courier, query: string) => item.name.toLowerCase().includes(query.toLowerCase())}

									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						// @ts-expect-error - form is not typed
						control={form.control}
						name="courierBranch"
						render={({ field }) => (
							<FormItem>
								<FormControl>
									<SearchDropdown
										field={field}
										data={courierOfficeQuery.data?.data.data}
										isPending={courierOfficeQuery.isPending}
										isError={courierOfficeQuery.isError}
										placeholder="Select courier branch"
										searchPlaceholder="Search courier branch"
										renderItem={courierRender}
										filterFunction={(item: Courier, query: string) => item.name.toLowerCase().includes(query.toLowerCase())}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						// @ts-expect-error - form is not typed
						control={form.control}
						name="default"
						render={({ field }) => (
							<FormItem>
								<FormControl>
									<div className="space-y-4">
										<div className="flex items-center gap-2">
											<Checkbox className="rounded-full" {...field} value={field.value.toString()} />
											<Label htmlFor="default">Set as default location</Label>
										</div>
										<div className="text-xs text-muted-foreground block">When you set as default location, you won't need to enter location every time you want to pickup an order. You can change this later in Settings</div>
									</div>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</form>
			</Form>
			<DialogFooter>
				{/* @ts-expect-error - form is not typed */}
				<Button className="w-full" onClick={form.handleSubmit(handleSubmit)}>Continue to pay</Button>
			</DialogFooter>
		</>
	);
}
